import classNames from "classnames";
import SelectableInput from "components/SelectableInput/SelectableInput";
import { useEffect } from "react";
import PlacesAutocomplete from "react-places-autocomplete";
import { Button, CardBody, Input } from "reactstrap";
import Constants from "utils/Constants";
import { handleLocationSelection } from "./LocationHelperFunctions";
import LocationSuggestions from "./LocationSuggestions";

const LocationEditor = ({
  location,
  setLocation,
  setOptionsOpen,
  setEditorOpen,
  fullScreen,
  customAddress,
  setCustomAddress,
  setSearchText,
  setIsEditing,
  setIsGoogleLocation,
  onAddLocation,
}) => {
  useEffect(() => {
    if (!location.Address.Address1 && !location.Address.StreetNumber) {
      return setCustomAddress("");
    }

    if (location.Address.Address1 && !location.Address.StreetNumber) {
      setCustomAddress(location.Address.Address1);
      return;
    }

    setCustomAddress(
      location.Address.StreetNumber + " " + location.Address.Address1
    );
    //eslint-disable-next-line
  }, [location.Address.Address1, location.Address.StreetNumber]);

  return (
    <>
      {fullScreen && (
        <div className="mx-2 mt-2">
          <div className="d-flex">
            <h5 className="mb-0">Address 1</h5>
            <input style={{ width: 1, height: 1, opacity: 0 }} />
          </div>
          <PlacesAutocomplete
            value={customAddress}
            onChange={handleCustomAddressChange}
            onSelect={handleSelect}
            highlightFirstSuggestion
            debounce={500}
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => (
              <div style={{ position: "relative" }}>
                <Input
                  bsSize="sm"
                  className="mb-2"
                  {...getInputProps({
                    placeholder: "Address 1",
                  })}
                />
                <CardBody
                  className={classNames("p-0", {
                    border: suggestions?.length > 0,
                  })}
                  style={{
                    zIndex: 99999,
                    position: "absolute",
                    top: 30,
                    left: 25,
                    width: "calc(100% - 65px)",
                  }}
                >
                  <LocationSuggestions
                    suggestions={suggestions}
                    loading={loading}
                    getSuggestionItemProps={getSuggestionItemProps}
                  />
                </CardBody>
              </div>
            )}
          </PlacesAutocomplete>
          <h5 className="mb-0">Address 2</h5>
          <Input
            bsSize="sm"
            placeholder="Address 2"
            value={location.Address.Address2}
            name="Address2"
            onChange={handleAddressChange}
            className="mb-2"
          />
          <div className="d-flex">
            <div className="mr-2 mb-2">
              <h5 className="mb-0">City</h5>
              <Input
                bsSize="sm"
                placeholder="City"
                value={location.Address.City}
                name="City"
                onChange={handleAddressChange}
              />
            </div>
            <div>
              <h5 className="mb-0">State</h5>
              <Input
                bsSize="sm"
                placeholder="State"
                value={location.Address.StateProvince}
                name="StateProvince"
                onChange={handleAddressChange}
              />
            </div>
          </div>

          <div className="d-flex">
            <div className="mr-2 mb-0">
              <h5 className="mb-0">Zip Code</h5>
              <Input
                bsSize="sm"
                placeholder="Zip Code"
                value={location.Address.ZipCode}
                name="ZipCode"
                onChange={handleAddressChange}
              />
            </div>
            <div>
              <h5 className="mb-0">Country</h5>
              <Input
                bsSize="sm"
                placeholder="Country"
                value={location.Address.Country}
                name="Country"
                onChange={handleAddressChange}
              />
            </div>
          </div>
          <hr className="mt-3 mb-0" />
        </div>
      )}

      <div className="mx-2 mb-2 mt-2">
        <h5 className="mb-1">Type</h5>
        <SelectableInput
          field={{ Name: "Type" }}
          value={location?.Type}
          options={Constants.LOCATION_TYPE_OPTIONS}
          onSelect={(newType) =>
            handleChange({ target: { name: "Type", value: newType } })
          }
          onDeselect={() =>
            handleChange({ target: { name: "Type", value: null } })
          }
          onClear={() =>
            handleChange({ target: { name: "Type", value: null } })
          }
          modifyTrigger={"click"}
          onOpen={() => setOptionsOpen(true)}
          onClose={() => setOptionsOpen(false)}
          searchable={false}
        />
        <h5 className="mt-2 mb-1">Description</h5>
        <Input
          value={location.Description}
          type="textarea"
          placeholder="Description"
          name="Description"
          onChange={handleChange}
          bsSize="sm"
        />
        <div className="d-flex mt-3">
          <Button
            size="sm"
            outline
            className="ml-auto"
            onClick={() => setEditorOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="primary"
            disabled={!location.Readable}
            onClick={() => onAddLocation()}
          >
            Save
          </Button>
        </div>
      </div>
    </>
  );

  function handleCustomAddressChange(newAddress) {
    setCustomAddress(newAddress);

    const addressArray = newAddress.match(/^(\d+)\s(.+)$/);
    const streetNumber = addressArray?.[1];
    const streetName = addressArray?.[2];

    setIsGoogleLocation(false);
    setLocation((prev) => ({
      ...prev,
      Address: {
        ...prev.Address,
        Address1: streetName || "",
        StreetNumber: streetNumber ?? "",
      },
    }));
  }

  function handleChange(e) {
    const newLocation = {
      ...location,
      [e.target.name]: e.target.value,
    };

    setLocation(newLocation);
    setIsGoogleLocation(false);
  }

  function handleAddressChange(e) {
    const newLocation = {
      ...location,
      Address: { ...location.Address, [e.target.name]: e.target.value },
    };

    setLocation(newLocation);
    setIsGoogleLocation(false);
  }

  async function handleSelect(value) {
    const newLocation = await handleLocationSelection(value);

    setSearchText(value);
    setIsEditing(false);
    setIsGoogleLocation(true);

    setLocation((prevState) => ({
      ...prevState,
      ...newLocation,
    }));
  }
};

export default LocationEditor;
